import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import constants from '@shared-styles/constants.module.scss';
import SkeletonWrapper from '../skeleton/SkeletonWrapper';
import NotEnoughDataMessage from '../NotEnoughDataMessage';
import { getXAxisLabel, getYAxisLabel } from '../GraphLabels';
import { itemSorterFn } from '../../../../utils/chartUtils';

const LEGEND_ORDER = {
  Satisfied: 1,
  Partial: 2,
  Deficient: 3
};

const buildPracticeData = practiceArea => {
  if (!practiceArea) {
    return [];
  }
  const data = [...practiceArea.Practices];
  data.sort((a, b) => a.Level - b.Level || a.NumberInLevel - b.NumberInLevel);
  return data.map(practice => ({ ...practice, Acronym: practiceArea.Acronym }));
};

/**
 * @typedef {object} PracticeAreaCharacterizationGraphProps
 * @property {Object} [practiceArea]
 * @property {(data: import('recharts').BarRectangleItem) => void} [practiceClick]
 * @property {boolean} loading
 */

/**
 * @param {PracticeAreaCharacterizationGraphProps} props
 */
const PracticeAreaCharacterizationGraph = props => {
  const { practiceArea, practiceClick, loading } = props;

  const data = buildPracticeData(practiceArea);

  const totalData = Math.max(
    ...data.map(practice => practice.Satisfied + practice.Partial + practice.Deficient)
  );

  const tooltipLabelFormatter = label => {
    const tooltipLabel = data.filter(x => x.Name === label);
    return tooltipLabel && tooltipLabel.length > 0 ? tooltipLabel[0].Acronym + ' ' + label : label;
  };

  // Need every PA in baseline appraisal
  return (
    <div className='baseline-graph characterization-graph__graph'>
      <SkeletonWrapper
        show={loading}
        renderComponent={() => (
          <>
            {totalData <= 2 ? <NotEnoughDataMessage /> : null}
            <div onClick={e => e.stopPropagation()}>
              <ResponsiveContainer width='95%' height={350}>
                <BarChart
                  data={data}
                  margin={{
                    top: 25,
                    right: 0,
                    left: -15,
                    bottom: 16
                  }}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='Name'>{getXAxisLabel('Practices within PA')}</XAxis>
                  {totalData >= 3 && (
                    <Tooltip
                      labelFormatter={label => tooltipLabelFormatter(label)}
                      itemSorter={itemSorterFn(LEGEND_ORDER)}
                    />
                  )}
                  <YAxis allowDecimals={false} domain={[0, totalData]}>
                    {getYAxisLabel('# Appraisals containing PA', 0, 25)}
                  </YAxis>
                  <Legend
                    wrapperStyle={{ paddingTop: '1.5rem' }}
                    align='center'
                    itemSorter={itemSorterFn(LEGEND_ORDER)}
                  />
                  {totalData >= 3 ? (
                    <Bar
                      dataKey='Satisfied'
                      stackId='a'
                      fill={constants.practiceSatisfiedColor}
                      cursor='pointer'
                      onClick={data => practiceClick(data)}
                    />
                  ) : null}
                  {totalData >= 3 ? (
                    <Bar
                      dataKey='Partial'
                      stackId='a'
                      fill={constants.practicePartialColor}
                      cursor='pointer'
                      onClick={data => practiceClick(data)}
                    />
                  ) : null}
                  {totalData >= 3 ? (
                    <Bar
                      dataKey='Deficient'
                      stackId='a'
                      fill={constants.practiceDeficientColor}
                      cursor='pointer'
                      onClick={data => practiceClick(data)}
                    />
                  ) : null}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}></SkeletonWrapper>
    </div>
  );
};

export default PracticeAreaCharacterizationGraph;
